<section class="market-positioning">
  <header class="market-positioning__header" *ngIf="hasHeaderContent">
    <h1 *ngIf="pageTitle">{{ pageTitle }}</h1>
    <p *ngIf="pageSubtitle">{{ pageSubtitle }}</p>
    <nav class="market-positioning__breadcrumb" *ngIf="breadcrumb.length">
      <ng-container *ngFor="let item of breadcrumb; let last = last">
        <span [class.market-positioning__breadcrumb-current]="last">{{ item.label }}</span>
        <span *ngIf="!last" class="market-positioning__breadcrumb-separator">{{ data?.breadcrumbSeparator }}</span>
      </ng-container>
    </nav>
  </header>

  <div class="market-positioning__surface">
    <div class="market-positioning__toolbar">
      <div class="market-positioning__filters">
        <button
          *ngFor="let option of ratingFilters"
          type="button"
          class="market-positioning__pill"
          [class.market-positioning__pill--active]="selectedRating === option.value"
          [ngStyle]="getFilterStyle(option)"
          (click)="selectRating(option.value)"
        >
          {{ option.label }}
        </button>

        <div
          class="market-positioning__select market-positioning__select--custom"
          *ngIf="showSegmentFilter"
          [ngStyle]="segmentFilterStyle"
          (click)="$event.stopPropagation()"
        >
          <button
            type="button"
            class="market-positioning__select-trigger"
            [attr.aria-label]="segmentPlaceholder"
            [attr.aria-expanded]="isSegmentDropdownOpen"
            (click)="toggleSegmentDropdown($event)"
          >
            <span class="market-positioning__select-label">{{ selectedSegmentLabel }}</span>
            <span class="market-positioning__select-icon">{{ data?.filters?.segment?.icon }}</span>
          </button>

          <div class="market-positioning__select-menu" *ngIf="isSegmentDropdownOpen">
            <div class="market-positioning__select-search-shell">
              <input
                type="text"
                class="market-positioning__select-search"
                [(ngModel)]="segmentSearchTerm"
                [placeholder]="segmentSearchPlaceholder"
                (click)="$event.stopPropagation()"
              />
            </div>
            <button
              *ngFor="let option of filteredSegmentOptions; trackBy: trackByOptionValue"
              type="button"
              class="market-positioning__select-option"
              [class.market-positioning__select-option--active]="selectedSegment === option.value"
              (click)="selectSegmentOption(option.value)"
            >
              {{ option.label }}
            </button>
          </div>
        </div>
      </div>

      <div class="market-positioning__actions" *ngIf="hasResetAction || hasSaveAction || hasExportAction">
        <button *ngIf="hasResetAction" type="button" class="market-positioning__button market-positioning__button--secondary" (click)="reset()">
          <span class="market-positioning__button-icon">{{ resetIcon }}</span>
          {{ resetLabel }}
        </button>
        <div class="market-positioning__save-action" *ngIf="hasSaveAction">
          <button
            type="button"
            class="market-positioning__button market-positioning__button--primary"
            [disabled]="isSaveActionDisabled"
            (click)="saveAllChanges()"
          >
            <span class="market-positioning__button-icon">{{ saveIcon }}</span>
            {{ saveLabel }}
          </button>
          <span class="market-positioning__save-message" [class.market-positioning__save-message--visible]="!!successMessage">
            {{ successMessage || data?.messages?.updateSuccess || 'Saved successfully.' }}
          </span>
        </div>
        <button *ngIf="hasExportAction" type="button" class="market-positioning__button market-positioning__button--primary" (click)="export()">
          <span class="market-positioning__button-icon">{{ exportIcon }}</span>
          {{ exportLabel }}
        </button>
      </div>
    </div>

    <p class="market-positioning__helper">{{ helperText }}</p>

    <aside class="market-positioning__legend">
      <strong>{{ legendTitle }}</strong>
      <ul>
        <li *ngFor="let item of legendItems">{{ item }}</li>
        <li
          class="market-positioning__legend-more-info"
          *ngIf="hasLegendMoreInfo"
          tabindex="0"
        >
          <span class="market-positioning__legend-more-trigger">
            {{ legendMoreInfoLabel }}
          </span>
          <div class="market-positioning__legend-preview" [ngStyle]="legendMoreInfoPreviewStyle" aria-hidden="true">
            <ng-container *ngIf="hasStructuredLegendMoreInfo; else legendPreviewImage">
              <div class="market-positioning__legend-card">
                <div class="market-positioning__legend-table-wrap">
                  <div class="market-positioning__legend-example-badge">{{ legendMoreInfoExampleTitle }}</div>
                  <table class="market-positioning__legend-table">
                    <colgroup>
                      <col class="market-positioning__legend-col--axis-pill" />
                      <col class="market-positioning__legend-col--axis-copy" />
                      <col class="market-positioning__legend-col--factor" />
                      <col class="market-positioning__legend-col--source" />
                      <col class="market-positioning__legend-col--weak" />
                      <col class="market-positioning__legend-col--fair" />
                      <col class="market-positioning__legend-col--good" />
                      <col class="market-positioning__legend-col--gap" />
                      <col class="market-positioning__legend-col--customer-value" />
                      <col class="market-positioning__legend-col--customer-score" />
                    </colgroup>
                    <thead>
                      <tr>
                        <th colspan="2" class="market-positioning__legend-table-spacer"></th>
                        <th>Factor</th>
                        <th>Source</th>
                        <th class="market-positioning__legend-cell--weak">Weak</th>
                        <th class="market-positioning__legend-cell--fair">Fair</th>
                        <th class="market-positioning__legend-cell--good">Good</th>
                        <th class="market-positioning__legend-table-spacer market-positioning__legend-gap-cell"></th>
                        <th class="market-positioning__legend-cell--customer-value">Customer Value</th>
                        <th>Customer Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        *ngFor="let row of legendMoreInfoRowsWithAxisSpan"
                        [class.market-positioning__legend-table-row-break]="row.axis"
                      >
                        <ng-container *ngIf="row.axisRowSpan">
                          <td
                            [attr.rowspan]="row.axisRowSpan"
                            class="market-positioning__legend-axis-pill-cell"
                            [class.market-positioning__legend-axis-pill-cell--internal]="row.axisSection?.tone === 'internal'"
                          >
                            <span class="market-positioning__legend-axis-pill-text">{{ row.axisSection?.label }}</span>
                          </td>
                          <td [attr.rowspan]="row.axisRowSpan" class="market-positioning__legend-axis-copy-cell">
                            <div class="market-positioning__legend-axis-title">{{ row.axisSection?.title }}</div>
                            <div class="market-positioning__legend-axis-subtitle">{{ row.axisSection?.subtitle }}</div>
                          </td>
                        </ng-container>
                        <td>{{ row.factor }}</td>
                        <td>{{ row.source }}</td>
                        <ng-container *ngIf="row.weak === row.fair && row.fair === row.good; else scaleCells">
                          <td colspan="3" class="market-positioning__legend-cell--merged">{{ row.weak }}</td>
                        </ng-container>
                        <ng-template #scaleCells>
                          <td class="market-positioning__legend-cell--weak">{{ row.weak }}</td>
                          <td class="market-positioning__legend-cell--fair">{{ row.fair }}</td>
                          <td class="market-positioning__legend-cell--good">{{ row.good }}</td>
                        </ng-template>
                        <td class="market-positioning__legend-table-spacer market-positioning__legend-gap-cell"></td>
                        <td class="market-positioning__legend-cell--customer-value">{{ row.customerValue }}</td>
                        <td
                          class="market-positioning__legend-cell--customer-score"
                          [class.market-positioning__legend-score--good]="row.customerScore.toLowerCase() === 'good'"
                          [class.market-positioning__legend-score--fair]="row.customerScore.toLowerCase() === 'fair'"
                          [class.market-positioning__legend-score--weak]="row.customerScore.toLowerCase() === 'weak'"
                        >
                          {{ row.customerScore }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div class="market-positioning__legend-note" *ngIf="legendMoreInfoNote">{{ legendMoreInfoNote }}</div>
              </div>
            </ng-container>

            <ng-template #legendPreviewImage>
              <img
                class="market-positioning__legend-preview-image"
                [src]="legendMoreInfoImageSrc"
                [alt]="legendMoreInfoAlt"
              />
            </ng-template>
          </div>
        </li>
      </ul>
    </aside>

    <div class="market-positioning__state" *ngIf="loading">{{ data?.messages?.loading }}</div>
    <div class="market-positioning__state market-positioning__state--error" *ngIf="!loading && errorMessage">
      {{ errorMessage }}
    </div>

    <div class="market-positioning__table-shell" *ngIf="!loading && !errorMessage" [ngStyle]="tableShellStyle">
      <table class="market-positioning__table">
        <colgroup>
          <col *ngFor="let column of visibleColumns" [style.width]="column.width || null" />
        </colgroup>
        <thead>
          <tr>
            <th *ngFor="let column of visibleColumns" [ngStyle]="getColumnStyle(column)">
              {{ column.header }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let row of filteredRows">
            <td *ngFor="let column of visibleColumns" [ngStyle]="getColumnStyle(column)">
              <ng-container *ngIf="column.type === 'label'; else notLabelCell">
                <span class="market-positioning__row-label">{{ getCellLabel(row, column) }}</span>
              </ng-container>

              <ng-template #notLabelCell>
                <ng-container *ngIf="column.type === 'history'; else ratingCell">
                  <span
                    class="cell-action-wrapper"
                    (mouseenter)="onHistoryMouseEnter(row, column, $event)"
                    (mouseleave)="onHistoryMouseLeave(row, column)"
                  >
                    <button class="cell-action icon-only" type="button" aria-label="History" (click)="onHistoryClick(row, column, $event)">
                      <span [innerHTML]="getHistoryActionLabel(column)"></span>
                    </button>

                    <div
                      class="cell-hover-popover cell-history-popover"
                      *ngIf="isHistoryPopoverActive(row, column) && getHistoryPopover(row, column) as historyPopover"
                      [style.top.px]="hoverPopoverPosition?.top"
                      [style.right.px]="hoverPopoverPosition?.right"
                      [style.max-height.px]="hoverPopoverMaxHeight"
                      role="tooltip"
                    >
                      <div class="history-popover-header">
                        <span class="material-icons history-popover-icon">history</span>
                        <span class="history-popover-title">{{ getHistoryPopoverTitle(row, column) }}</span>
                        <button type="button" class="history-popover-close" (click)="closeHoverPopover()" aria-label="Close history">
                          <span class="material-icons">close</span>
                        </button>
                      </div>

                      <div class="history-popover-meta">
                        <div class="history-popover-meta-item">
                          <span class="history-popover-meta-label">Customer ID:</span>
                          <span class="history-popover-meta-value">{{ historyPopover.customerId }}</span>
                        </div>
                        <div class="history-popover-meta-item">
                          <span class="history-popover-meta-label">Updated By:</span>
                          <span class="history-popover-meta-value">{{ historyPopover.updatedBy }}</span>
                        </div>
                        <div class="history-popover-meta-item history-popover-meta-item--full">
                          <span class="history-popover-meta-label">Latest Updated Date:</span>
                          <span class="history-popover-meta-value">{{ formatHistoryDate(historyPopover.updatedDate) }}</span>
                        </div>
                      </div>

                      <div class="history-popover-section-title">
                        <span class="material-icons">list_alt</span>
                        Changed History
                      </div>

                      <div class="history-popover-versions">
                        <div class="history-version-card" *ngFor="let version of historyPopover.versions">
                          <div class="history-version-header" (click)="toggleHistoryVersion(column, version, $event)">
                            <span class="history-version-meta">Updated By: <strong>{{ version.updatedBy }}</strong></span>
                            <span class="history-version-meta">Updated Date: <strong>{{ formatHistoryDate(version.updatedDate) }}</strong></span>
                            <span class="history-changes-badge">Changes ({{ version.changes.length }})</span>
                            <span class="material-icons history-version-toggle">{{ isHistoryVersionExpanded(column, version) ? 'expand_less' : 'expand_more' }}</span>
                          </div>
                          <div class="history-version-body" *ngIf="isHistoryVersionExpanded(column, version)">
                            <table class="history-changes-table">
                              <thead>
                                <tr>
                                  <th>Changed Field</th>
                                  <th>Previous Value</th>
                                  <th>New Value</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr *ngFor="let change of version.changes">
                                  <td>{{ change.field }}</td>
                                  <td class="history-value-previous">{{ change.previousValue }}</td>
                                  <td class="history-value-new">{{ change.newValue }}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>

                      <div class="history-popover-legend">
                        <span class="history-legend-dot history-legend-dot--previous"></span>
                        <span>Previous Value</span>
                        <span class="material-icons history-legend-arrow">arrow_forward</span>
                        <span class="history-legend-dot history-legend-dot--new"></span>
                        <span>New Value</span>
                      </div>
                    </div>

                    <div
                      class="cell-hover-popover cell-history-popover history-popover-status"
                      *ngIf="isHistoryPopoverActive(row, column) && !getHistoryPopover(row, column)"
                      [style.top.px]="hoverPopoverPosition?.top"
                      [style.right.px]="hoverPopoverPosition?.right"
                      role="tooltip"
                    >
                      <span *ngIf="isHistoryLoading(row, column)">Loading history...</span>
                      <span *ngIf="!isHistoryLoading(row, column) && hasFetchedHistory(row, column)">No history available for this customer.</span>
                    </div>
                  </span>
                </ng-container>
              </ng-template>

              <ng-template #ratingCell>
                <ng-container *ngIf="getCell(row, column) as cell">
                  <div class="market-positioning__cell" [ngStyle]="getCellContentStyle(column)">
                    <div class="market-positioning__cell-top">
                      <span class="market-positioning__badge" [ngStyle]="getBadgeStyle(cell.rating)">
                        {{ getRatingConfig(cell.rating).label }}
                      </span>
                      <button
                        *ngIf="column.editable"
                        type="button"
                        class="market-positioning__edit"
                        [attr.aria-label]="data?.editBehavior?.ariaLabel"
                        [attr.aria-expanded]="isRatingEditorOpen(row, column)"
                        (click)="toggleRatingEditor(row, column, $event)"
                      >
                        {{ data?.editBehavior?.icon }}
                      </button>
                      <div
                        *ngIf="column.editable && isRatingEditorOpen(row, column)"
                        class="market-positioning__rating-menu"
                        (click)="$event.stopPropagation()"
                      >
                        <button
                          *ngFor="let option of ratingEditorOptions"
                          type="button"
                          class="market-positioning__rating-option"
                          [class.market-positioning__rating-option--active]="cell.rating === option.value"
                          [ngStyle]="getRatingOptionStyle(option, cell.rating)"
                          (click)="chooseRating(row, column, option.value, $event)"
                        >
                          {{ option.label }}
                        </button>
                      </div>
                      <button
                        *ngIf="shouldShowReasonButton(row, column)"
                        type="button"
                        class="market-positioning__save market-positioning__reason-toggle"
                        [class.market-positioning__reason-toggle--open]="isReasonEditorOpen(row, column)"
                        [attr.aria-label]="data?.editReason?.ariaLabel || 'Add edit reason'"
                        [attr.aria-expanded]="isReasonEditorOpen(row, column)"
                        (click)="toggleReasonEditor(row, column, $event)"
                      >
                        {{ getReasonIcon(row, column) }}
                      </button>
                      <button
                        *ngIf="column.editable && isCellDirty(row, column)"
                        type="button"
                        class="market-positioning__save"
                        [class.market-positioning__save--saving]="isCellSaving(row, column)"
                        [disabled]="isCellSaving(row, column)"
                        [attr.aria-label]="data?.updateAction?.ariaLabel || 'Save market positioning update'"
                        (click)="saveCell(row, column)"
                      >
                        {{ isCellSaving(row, column) ? (data?.updateAction?.savingIcon || '...') : (data?.updateAction?.icon || '✓') }}
                      </button>
                      <span
                        *ngIf="isCellUpdated(row, column)"
                        class="market-positioning__badge market-positioning__updated"
                        style="background:#edf6ff;color:#1d62ad;border-color:#6da7e8;font-size:10px;min-width:auto;text-transform:capitalize;margin-left:auto"
                      >
                        {{ getCellUpdateLabel(row, column) || data?.editReason?.updatedLabel || 'Updated' }}
                      </span>
                      <div
                        *ngIf="shouldShowReasonButton(row, column) && isReasonEditorOpen(row, column)"
                        class="market-positioning__reason-popover"
                        (click)="$event.stopPropagation()"
                      >
                        <textarea
                          class="market-positioning__reason-input"
                          [ngModel]="getEditReason(row, column)"
                          (ngModelChange)="setEditReason(row, column, $event)"
                          [placeholder]="data?.editReason?.placeholder || 'Write reason and press Enter'"
                          (keydown)="onReasonKeydown(row, column, $event)"
                        ></textarea>
                      </div>
                    </div>
                    <div class="market-positioning__progress" *ngIf="shouldShowProgress(cell)">
                      <span [ngStyle]="getProgressStyle(cell)"></span>
                    </div>
                    <span class="market-positioning__percent" *ngIf="shouldShowProgress(cell)">
                      {{ cell.confidence }}{{ data?.table?.progress?.suffix }}
                    </span>
                    <div
                      class="market-positioning__percent"
                      style="max-width:110px;overflow-wrap:anywhere;color:#1d62ad"
                      *ngIf="getSavedEditReason(row, column)"
                    >
                      Reason: {{ getSavedEditReason(row, column) }}
                    </div>
                  </div>
                </ng-container>
              </ng-template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</section>
